// src/context/WorkflowNavigationContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WorkflowNavigationContext = createContext();

const STORAGE_KEY = 'workflowNavContext';
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

export const useWorkflowNavigation = () => useContext(WorkflowNavigationContext);

export const WorkflowNavigationProvider = ({ children }) => {
    const [workflowContext, setWorkflowContextState] = useState(null);

    // Load from sessionStorage on mount
    useEffect(() => {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Check if expired
                if (parsed.timestamp && Date.now() - parsed.timestamp < EXPIRY_MS) {
                    setWorkflowContextState(parsed);
                } else {
                    sessionStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (e) {
            console.error('Failed to parse workflow navigation context:', e);
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    // Set workflow context (when navigating from workflow to job)
    const setWorkflowContext = useCallback((context) => {
        const enrichedContext = {
            ...context,
            timestamp: Date.now(),
        };
        setWorkflowContextState(enrichedContext);
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(enrichedContext));
        } catch (e) {
            console.error('Failed to save workflow navigation context:', e);
        }
    }, []);

    // Clear workflow context
    const clearWorkflowContext = useCallback(() => {
        setWorkflowContextState(null);
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error('Failed to clear workflow navigation context:', e);
        }
    }, []);

    return (
        <WorkflowNavigationContext.Provider value={{
            workflowContext,
            setWorkflowContext,
            clearWorkflowContext,
        }}>
            {children}
        </WorkflowNavigationContext.Provider>
    );
};
